<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { DiscussionComment, DiscussionProperties, UserInfoDto } from "~/api";
import { formatDate } from "~/utils/dateUtils";
import { plural } from "~/utils/miscUtils";
import DiscussionThreadComment from "./DiscussionThreadComment.vue";

const props = defineProps<{
  comment: DiscussionComment;
  colorHierarchy: string[];
  authorColorMap?: Record<string, string>;
  currentUser: UserInfoDto;
  currentUserGuid: string;
  properties: DiscussionProperties;
  readOnly: boolean;
  currentDepth: number;
  substituteTemplatedText: (
    text: string | undefined | null,
  ) => string | undefined | null;
}>();

const emit = defineEmits<{
  (e: "reply", parentId: number, reply: DiscussionComment): void;
}>();

const showReply = ref(false);
const replyText = ref("");
const editing = ref<DiscussionComment>();
const collapsed = ref(props.properties?.startCollapsed || false);
const maxDepth = 5;

const getAuthorColor = (author: string) => {
  if (!props.authorColorMap) {
    return props.colorHierarchy[0];
  }
  return props.authorColorMap[author] || props.colorHierarchy[0];
};

const commentColor = computed(() => getAuthorColor(props.comment.authorGuid));

const submitReply = () => {
  const reply: DiscussionComment = {
    id: editing.value ? editing.value.id : Date.now(),
    authorName: props.currentUser.loginName,
    authorGuid: props.currentUser.userGuid,
    text: replyText.value,
    createdAt: new Date().toISOString(),
    editedAt: editing.value ? new Date().toISOString() : undefined,
    replies: editing.value ? editing.value.replies : [],
  };

  emit("reply", props.comment.id, reply);
  replyText.value = "";
  showReply.value = false;
  editing.value = undefined;

  collapsed.value = false; // Expand the comment thread after replying
};

const handleCancel = () => {
  showReply.value = false;
  replyText.value = "";
  editing.value = undefined;
};

/**
 * These watchers are used to scroll to the reply area when the user clicks on the reply/edit button.
 * This prevents confusion if the user clicks on the reply button and the reply area is not visible.
 */
watch(
  () => editing.value,
  async (newComment) => {
    if (newComment) {
      await nextTick(); // need to wait for DOM to update
      const el = document.getElementById("discussion-reply-input-area");
      replyText.value = newComment.text;
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus();
      }
    }
  },
);

watch(
  () => showReply.value,
  async (newValue) => {
    if (newValue) {
      await nextTick(); // need to wait for DOM to update
      const el = document.getElementById("discussion-reply-area");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  },
);

/**
 * Returns the total number of replies for a comment, including nested replies.
 * @param comment The comment object to get the total replies for.
 */
const getTotalReplies = (comment: DiscussionComment): number => {
  if (!comment.replies) return 0;
  return (
    comment.replies.length +
    comment.replies.reduce((acc, reply) => acc + getTotalReplies(reply), 0)
  );
};
</script>

<template>
  <div
    class="DiscussionComponentComment"
    :style="{ borderLeftColor: commentColor }"
  >
    <div class="comment-container">
      <div class="meta">
        <div class="left-meta">
          <strong :style="{ color: commentColor }" class="author">
            {{ comment.authorName }}
          </strong>
          <span class="timestamp"
            >•
            {{
              comment.editedAt
                ? formatDate(comment.editedAt, 7)
                : formatDate(comment.createdAt, 7)
            }}</span
          >
          <el-popover
            v-if="comment.editedAt"
            trigger="hover"
            placement="top"
            width="fit-content"
            :popper-style="{ fontSize: '0.8em' }"
            :content="`Original post: ${formatDate(comment.createdAt, 7)}`"
          >
            <template #reference>
              <span class="edited-at"> (Edited) </span>
            </template>
          </el-popover>
        </div>

        <div v-if="!(showReply || editing)" class="buttons">
          <el-button
            v-if="comment.authorGuid === currentUser.userGuid && !readOnly"
            class="edit-btn"
            text
            @click="editing = props.comment"
          >
            Edit
          </el-button>
          <el-button
            v-if="currentDepth <= maxDepth && !readOnly"
            class="reply-btn"
            text
            @click="showReply = true"
          >
            Reply
          </el-button>
        </div>
      </div>

      <div v-if="!editing" class="text">
        <div>
          {{ comment.text }}
        </div>
      </div>

      <div
        v-if="showReply || editing"
        id="discussion-reply-area"
        class="reply-input"
      >
        <el-input
          id="discussion-reply-input-area"
          v-model="replyText"
          class="reply-input-area"
          type="textarea"
          :rows="3"
          :disabled="readOnly"
          :placeholder="substituteTemplatedText(properties.placeholder) ?? ''"
        />

        <el-button
          class="post-btn"
          type="primary"
          :disabled="!replyText.trim() || readOnly"
          @click="submitReply"
        >
          {{ properties.saveButtonText }}
        </el-button>

        <el-button
          text
          class="cancel-btn"
          :disabled="readOnly"
          @click="handleCancel"
        >
          {{ properties.cancelButtonText || "Cancel" }}
        </el-button>
      </div>
    </div>

    <div v-if="comment.replies?.length" class="reply-toggle-wrapper">
      <el-button
        :class="['collapse-btn', { expanded: !collapsed }]"
        text
        :bg="collapsed ? true : false"
        @click="collapsed = !collapsed"
      >
        {{ collapsed ? "View replies" : "Hide replies" }}
      </el-button>

      <span v-if="collapsed" class="reply-summary">
        {{
          `${getTotalReplies(comment)} repl${plural(getTotalReplies(comment), "y", "ies")} as of ${formatDate(comment.replies[comment.replies.length - 1].editedAt || comment.replies[comment.replies.length - 1].createdAt, 7)}`
        }}
      </span>
    </div>

    <div v-if="!collapsed && comment.replies?.length" class="replies">
      <DiscussionThreadComment
        v-for="reply in comment.replies"
        :key="reply.id"
        :comment="reply"
        :color-hierarchy="colorHierarchy"
        :author-color-map="authorColorMap"
        :current-user="currentUser"
        :current-user-guid="currentUserGuid"
        :current-depth="currentDepth + 1"
        :properties="properties"
        :read-only="readOnly"
        :substitute-templated-text="substituteTemplatedText"
        @reply="
          (parentId: number, reply: DiscussionComment) =>
            emit('reply', parentId, reply)
        "
      />
    </div>
  </div>
</template>

<style lang="less" scoped>
.DiscussionComponentComment {
  display: grid;
  margin: 1em 0;
  padding-left: 1em;
  border-left: 4px solid;
  border-radius: 6px;
  transition: border-color 0.3s ease;

  .comment-container {
    background: #fdfdfd;
    padding: 1em;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    .meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 0.9em;
      color: #555;
      margin-bottom: 0.25em;
      width: 100%;

      .author {
        font-weight: 600;
      }

      .buttons {
        display: flex;
        flex-wrap: nowrap;
      }
      .reply-btn,
      .edit-btn {
        justify-self: end;
        padding: 0 0.5em;

        max-width: fit-content;
      }

      .reply-btn,
      .edit-btn,
      .timestamp,
      .edited-at {
        margin-left: 0.5em;
        color: #888;
      }
    }
  }

  .reply-toggle-wrapper {
    display: flex;
    align-items: center;
    margin-top: 0.5em;

    &:has(.expanded) {
      justify-content: end;
    }
    .collapse-btn {
      color: #888;
      justify-self: start;
      max-width: fit-content;
    }

    .reply-summary {
      margin-left: 0.5em;
      font-size: 0.8em;
      color: #999;
    }
  }

  .text {
    margin: 0.5em 0;
    font-size: 1em;
    color: #333;
  }

  .replies {
    border-left: 2px dashed #ddd;
    padding-left: 1em;
  }
}
</style>
